import { schemas } from '#oaf_schema.registry';
import { createStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { IS_DEVELOPMENT } from '~/config';
import { makePayment } from '../../services/securityReviewPaymentService';
import { findSecurityReviewForPackageVersionId, getIsReReview, getSecurityReview, inheritPreviousVersionSecReview, upsertSecurityReview } from '../../services/securityReviewService';
import { getSolutionsById } from '../../services/solutionsService';
import { getUniqueIteratorKey, scrollToTop } from '../../utilities/browserUtilities';
import { handleException, throwDevelopmentOrLog } from '../../utilities/errorUtilities';
import { logger } from '../../utilities/logger';
import { notifyFailure, notifySuccess } from '../../utilities/notificationUtilities';
import { isApiSolutionType } from '../../utilities/schemaUtilities';
import { SecurityEditorStatus, getSecurityEditorStatus } from '../../utilities/securityEditorStatus';
import { PartnerSecurityReviewStatus, createChallenge, lastChallengeRaised, obtainSolutionId, removeEmptyReport, removeEmptyTestEnviroments, removeReport, updateSecurityReview } from '../../utilities/securityReviewUtilities';
import { SecurityReviewValidator } from '../../utilities/securityReviewValidator';
import { isEditable } from '../../utilities/solutionUtilities';
import { i18nStore } from '../i18nStore';
import { publisherStore, selectPublisher } from '../publisher/publisherStore';
import { SECURITY_REVIEW_EDITOR_ROUTE, SOLUTIONS_ROUTE, navigate, routerStore, selectMatch } from '../routerStore';
import { selectArchitectureReports, selectContactInformation, selectFalsePositiveInFailedSR, selectFalsePositivesReports, selectOtherDocsReports, selectRaiseAChallenge, selectReportItemsByType, selectReports, selectSFCodeAnalyzerReports, selectSampleApiReports, selectSecurityReview, selectSecurityScannerReports, selectTestEnviroments } from './securityReviewStoreSelectors';
import technologyDetailsActions from './technologyDetails';
import testEnvironmentActions from './testEnvironments';
import { klona } from 'klona/json';
export let REPORT_TYPE = /*#__PURE__*/function (REPORT_TYPE) {
  REPORT_TYPE["architectureAndUsage"] = "architectureAndUsage";
  REPORT_TYPE["sampleAPICallouts"] = "sampleAPICallouts";
  REPORT_TYPE["securityScanner"] = "securityScanner";
  REPORT_TYPE["falsePositives"] = "falsePositives";
  REPORT_TYPE["sfCodeAnalyzer"] = "sfCodeAnalyzer";
  REPORT_TYPE["otherDocs"] = "otherDocs";
  return REPORT_TYPE;
}({});
export const getDefaultReportItem = () => schemas['securityReview/plugins/Reports/ReportsItem'].new({
  iteratorKey: getUniqueIteratorKey()
});
const initialState = {
  securityReview: schemas['securityReview/BaseSecurityReview'].new({
    plugins: []
  }),
  securityReviewOps: schemas['securityReviewOps/BaseSecurityReviewOps'].new(),
  solution: schemas['solution/Solution'].new(),
  uploadingAssets: 0,
  hasChanged: false,
  isLoading: false,
  isSaving: false
};

/*
 * Re export child exports
 */
export * from './securityReviewStoreSelectors';
export * from './technologyDetails';
export * from './testEnvironments';
/* End re-export */
export const securityReviewStore = createStore(initialState, (set, get) => ({
  ...technologyDetailsActions(set),
  ...testEnvironmentActions(set),
  reset() {
    set(function reset() {
      return klona(initialState);
    });
  },
  change() {
    set(function change(draft) {
      draft.hasChanged = true;
    });
  },
  async initialize({
    id,
    solutionId,
    packageVersionId
  }) {
    set(function loading(draft) {
      draft.isLoading = true;
    });
    let securityReview = get().securityReview;
    let securityReviewOps = get().securityReviewOps;
    let solution = get().solution;
    let hasChanged = get().hasChanged;
    let packageVersionSecurityReview;
    if (packageVersionId && id === undefined && securityReview.id === undefined) {
      packageVersionSecurityReview = await findSecurityReviewForPackageVersionId(packageVersionId);
    }
    if (packageVersionSecurityReview && packageVersionSecurityReview.id !== undefined && packageVersionSecurityReview.id !== 'undefined' && packageVersionSecurityReview.id !== null && packageVersionSecurityReview.id) {
      navigate(`${SECURITY_REVIEW_EDITOR_ROUTE}?id=${packageVersionSecurityReview.id}`);
    }
    // The following else-if are not a pattern to follow. These were added as part of an investigation
    // These should be removed once the source of the issue is found
    else if (packageVersionSecurityReview && packageVersionSecurityReview.id === 'undefined') {
      console.error('SecurityReviewStore::initialize::', `packageVersionSecurityReview returned undefined as string for packageVersion=${packageVersionId}`);
    } else if (packageVersionSecurityReview && packageVersionSecurityReview.id === undefined) {
      console.error('SecurityReviewStore::initialize::', `packageVersionSecurityReview returned undefined for packageVersion=${packageVersionId}`);
    } else {
      var _solution;
      const editorStatus = getSecurityEditorStatus(id, securityReview);
      switch (editorStatus) {
        case SecurityEditorStatus.DifferentSecurityReview:
          {
            const payload = await getSecurityReview(id);
            securityReview = schemas['securityReview/BaseSecurityReview'].new(payload === null || payload === void 0 ? void 0 : payload.securityReview);
            securityReviewOps = schemas['securityReviewOps/BaseSecurityReviewOps'].new(payload === null || payload === void 0 ? void 0 : payload.securityReviewOps);
            solution = schemas['solution/Solution'].new(payload === null || payload === void 0 ? void 0 : payload.solution);
            hasChanged = false;
            break;
          }
        case SecurityEditorStatus.NewSecurityReview:
          {
            if (solutionId) {
              //If we got the solutionId then it's a API solution and we already have the
              //solution created so we do not need to obtain the solutionId
              securityReview = schemas['securityReview/BaseSecurityReview'].new({
                org62AccountId: publisherStore.getState().publisher.org62AccountId,
                status: PartnerSecurityReviewStatus.new,
                solution: solutionId
              });
              break;
            }
            const obtainedSecurityReview = await obtainSolutionId(packageVersionId);
            const inhertitedSR = await inheritPreviousVersionSecReview(obtainedSecurityReview.securityReview.solution);
            const isReReview = await getIsReReview(obtainedSecurityReview.securityReview.solution);
            if (inhertitedSR) {
              securityReview = {
                ...inhertitedSR,
                isReReview
              };
            } else {
              securityReview = {
                ...obtainedSecurityReview.securityReview,
                status: PartnerSecurityReviewStatus.new,
                org62AccountId: publisherStore.getState().publisher.org62AccountId,
                isReReview
              };
            }
            hasChanged = false;
            break;
          }
        default:
        // If we're not dealing with a new or different security review, we don't need to re-initialize the store
      }

      //If SR is editable we set the isSubmited field to false
      if (isEditable(securityReview.status, securityReviewOps.status)) {
        securityReview.isSubmitted = false;
      }
      if (!((_solution = solution) !== null && _solution !== void 0 && _solution.id)) {
        //We need to get the solution projection to get solution information
        solution = await getSolutionsById(securityReview.solution);
      }
      set(function initializing(draft) {
        draft.securityReview = securityReview;
        draft.securityReviewOps = securityReviewOps;
        draft.hasChanged = hasChanged;
        draft.solution = solution;
        // Running these selectors because they have logic
        // that initilizes the security review plugins
        selectReports(draft, true);
        selectTestEnviroments(draft, true);
        selectContactInformation(draft, true);
        selectRaiseAChallenge(draft, true);
      });
      set(function initialized(draft) {
        draft.isLoading = false;
      });
    }
  },
  upsertPlugin(plugin, draft) {
    if (draft) {
      const index = draft.plugins.findIndex(({
        pluginType
      }) => pluginType === plugin.pluginType);
      if (index === -1) {
        draft.plugins.push(plugin);
      } else {
        draft.plugins[index].data = plugin.data;
      }
    }
    return plugin;
  },
  setId(id) {
    set(function setId(draft) {
      draft.securityReview.id = id;
    });
  },
  setStatus(status) {
    set(function setStatus(draft) {
      draft.securityReview.status = status;
    });
  },
  submit(redirectTo = '') {
    securityReviewStore.saveSecurityReview({
      redirectAfterSave: true,
      redirectTo,
      submitReview: true
    });
  },
  setIsSubmitted(isSubmitted) {
    set(function setIsSubmitted(draft) {
      draft.securityReview.isSubmitted = isSubmitted;
    });
  },
  setPrimaryContactEmail(email) {
    set(function setPrimaryContactEmail(draft) {
      selectContactInformation(draft, true).primaryEmail = email;
    });
  },
  setSecondaryContactEmail(email) {
    set(function setSecondaryContactEmail(draft) {
      selectContactInformation(draft, true).secondaryEmail = email;
    });
  },
  setContactName(name) {
    set(function setContactName(draft) {
      selectContactInformation(draft, true).name = name;
    });
  },
  setSolutionId(id) {
    set(function setSolutionId(draft) {
      draft.securityReview.solution = id;
    });
  },
  setNoFalsePositivesConfirmation(noFalsePositivesConfirmation) {
    set(function setNoFalsePositivesConfirmation(draft) {
      selectReports(draft, true).noFalsePositivesConfirmation = noFalsePositivesConfirmation;
    });
  },
  setNoSFCondeAnalyzerConfirmation(noSFCodeAnalyzerConfirmation) {
    set(function setNoSFCondeAnalyzerConfirmation(draft) {
      selectReports(draft, true).noSFCodeAnalyzerConfirmation = noSFCodeAnalyzerConfirmation;
      if (noSFCodeAnalyzerConfirmation) {
        removeReport(selectSFCodeAnalyzerReports(draft));
      }
    });
  },
  setNoSFCodeAnalyzerReasons(reasons) {
    set(function setNoSFCodeAnalyzerReasons(draft) {
      selectReports(draft, true).noSFCodeAnalyzerReasons = reasons;
    });
  },
  setRaiseAChallengeText(text) {
    set(function setRaiseAChallengeText(draft) {
      const challenge = selectRaiseAChallenge(draft);
      if (challenge.challengeRaised && challenge.challenges) {
        const lastChallenge = lastChallengeRaised(draft.securityReview);
        if (text === '') {
          challenge.challengeRaised = false;
          challenge.challenges.shift();
        } else if (lastChallenge) {
          lastChallenge.challenge = text;
        }
      } else {
        var _challenge$challenges;
        (_challenge$challenges = challenge.challenges) === null || _challenge$challenges === void 0 ? void 0 : _challenge$challenges.push(createChallenge(text));
        challenge.challengeRaised = true;
      }
    });
  },
  incrementUploadingAssets() {
    set(function incrementUploadingAssets(draft) {
      draft.uploadingAssets++;
    });
  },
  decrementUploadingAssets() {
    set(function decrementUploadingAssets(draft) {
      draft.uploadingAssets--;
      if (draft.uploadingAssets < 0) {
        draft.uploadingAssets = 0;
      }
    });
  },
  addReportItem(item = getDefaultReportItem(), reportType) {
    set(function addReportItem(draft) {
      const reportItems = selectReportItemsByType(draft, reportType);
      reportItems.push(item);
    });
  },
  editReportItem(item, reportType, index) {
    set(function editReportItem(draft) {
      const reportItems = selectReportItemsByType(draft, reportType);
      if (reportItems) {
        if (!reportItems[index]) {
          throwDevelopmentOrLog(new Error(`Failed to find Report item at index '${index}'`));
          return;
        }
        reportItems[index] = {
          ...reportItems[index],
          ...item
        };
      }
    });
  },
  removeReportItem(reportType, index) {
    set(function removeReportItem(draft) {
      const reportItems = selectReportItemsByType(draft, reportType);
      if (reportItems) {
        reportItems.splice(index, 1);
      }
    });
  },
  addFalsePositivesFailedSR(item) {
    set(function addFalsePositivesFailedSR(draft) {
      const falsePositive = selectFalsePositiveInFailedSR(draft, true);
      falsePositive.fileName = item.fileName;
      falsePositive.mediaId = item.mediaId;
    });
  },
  removeFalsePositivesFailedSR() {
    set(function removeFalsePositivesFailedSR(draft) {
      const index = draft.securityReview.plugins.findIndex(({
        pluginType
      }) => pluginType === 'securityReview/plugins/FalsePositive');
      if (index !== -1) {
        draft.securityReview.plugins.splice(index, 1);
      }
    });
  },
  removeEmptyValues() {
    set(function removeEmptyValues(draft) {
      const removeEmptyReportsList = [selectArchitectureReports(draft), selectSampleApiReports(draft), selectSecurityScannerReports(draft), selectFalsePositivesReports(draft), selectSFCodeAnalyzerReports(draft), selectOtherDocsReports(draft)];
      removeEmptyReport(removeEmptyReportsList);
      removeEmptyTestEnviroments(selectTestEnviroments(draft));
    });
  },
  changeSROpsStatus(status) {
    set(function changeSROpsStatus(draft) {
      draft.securityReviewOps.status = status;
    });
  },
  async createPayment() {
    if (!get().securityReview.id) {
      await securityReviewStore.saveSecurityReview({
        redirectAfterSave: false,
        notifySuccessOpt: false
      });
    }
    await makePayment(get().securityReview.id || '', selectContactInformation(get()).primaryEmail);
  },
  async saveSecurityReview({
    redirectAfterSave = true,
    redirectTo,
    notifySuccessOpt = true,
    submitReview = false
  } = {}) {
    let succeeded = false;
    securityReviewStore.saveOnlyTechDetailsToggleOn();
    securityReviewStore.removeEmptyValues();
    set(function beginSave(draft) {
      draft.isSaving = true;
    });
    let obtainedSecurityReview;
    let srPreviousState = {
      status: '',
      isSubmitted: ''
    };
    try {
      const securityReviewStateSnapshot = get();
      obtainedSecurityReview = updateSecurityReview(get().securityReview, securityReviewStateSnapshot);
      //storing SR status here since .resetState() is keeping the reference and not updating with the correct values when handling exceptions.
      srPreviousState = {
        status: obtainedSecurityReview.securityReview.status,
        isSubmitted: obtainedSecurityReview.securityReview.isSubmitted
      };
      if (submitReview) {
        // After a SR is Submitted, it doesnt matter how many times we re-submit it, status should still be "Re-Submitted"
        if (obtainedSecurityReview.securityReview.status === PartnerSecurityReviewStatus.submitted || obtainedSecurityReview.securityReview.status === PartnerSecurityReviewStatus.reSubmitted) {
          obtainedSecurityReview.securityReview.status = PartnerSecurityReviewStatus.reSubmitted;
        } else {
          obtainedSecurityReview.securityReview.status = PartnerSecurityReviewStatus.submitted;
        }
        obtainedSecurityReview.securityReview.isSubmitted = true;
      }
      const response = await upsertSecurityReview(obtainedSecurityReview.securityReview);
      if (response !== null && response !== void 0 && response.id) {
        var _obtainedSecurityRevi, _securityReviewStateS, _securityReviewStateS2, _securityReviewStateS3;
        securityReviewStore.setId(response.id);
        (_obtainedSecurityRevi = obtainedSecurityReview) === null || _obtainedSecurityRevi === void 0 ? void 0 : _obtainedSecurityRevi.setSecurityReviewId(response.id);
        //If it's submitted and SROPs is Returned ops then we are gonna flip ops to new so it's read only again
        if ((_securityReviewStateS = securityReviewStateSnapshot.securityReview) !== null && _securityReviewStateS !== void 0 && _securityReviewStateS.isSubmitted && (((_securityReviewStateS2 = securityReviewStateSnapshot.securityReviewOps) === null || _securityReviewStateS2 === void 0 ? void 0 : _securityReviewStateS2.status) === 'Returned - Ops' || ((_securityReviewStateS3 = securityReviewStateSnapshot.securityReviewOps) === null || _securityReviewStateS3 === void 0 ? void 0 : _securityReviewStateS3.status) === 'Failed - ProdSec')) {
          securityReviewStore.changeSROpsStatus('Processing - Ops');
        }
        logger.debug(`Saved security review: ${response.id}`);
        if (notifySuccessOpt) {
          notifySuccess(i18nStore.getState().i18nStaticCache.notifications.securityReviewSaved);
        }
        succeeded = true;
        set(function updateSecurityReviewWithOAFsResponse(draft) {
          draft.securityReview = response;
        });
      }
    } catch (exception) {
      var _obtainedSecurityRevi2, _response;
      (_obtainedSecurityRevi2 = obtainedSecurityReview) === null || _obtainedSecurityRevi2 === void 0 ? void 0 : _obtainedSecurityRevi2.resetState();
      obtainedSecurityReview.securityReview.status = srPreviousState.status;
      obtainedSecurityReview.securityReview.isSubmitted = srPreviousState.isSubmitted;
      handleException(exception, 'securityReview.saveSecurityReview');
      if ((_response = exception.response) !== null && _response !== void 0 && _response.data.error.find(err => err.name === 'security_review_already_exists' && err.stack === 'solution')) {
        notifyFailure(i18nStore.getState().i18nStaticCache.notifications.securityReviewAlreadyExists);
      } else {
        notifyFailure(i18nStore.getState().i18nStaticCache.notifications.genericError);
      }
    }
    set(function endSave(draft) {
      draft.isSaving = false;
      if (succeeded) {
        draft.hasChanged = false;
      }
    });
    if (succeeded) {
      if (redirectAfterSave) {
        navigate(redirectTo ? redirectTo : SOLUTIONS_ROUTE);
      }
    }
    return succeeded;
  }
}), 'SECURITY_REVIEW', IS_DEVELOPMENT);

// Initialize the store only when we have a publisher and we're in the right page
function checkShouldInitialize() {
  const {
    publisher
  } = publisherStore.getState();
  const {
    url,
    queryString
  } = routerStore.getState();
  if (!publisher || !url || !SECURITY_REVIEW_EDITOR_ROUTE.includes(url)) {
    securityReviewStore.reset();
    return;
  }
  let id;
  let packageVersionId;
  let solutionId;
  if (queryString) {
    var _params$get, _params$get2, _params$get3;
    const params = new URLSearchParams(queryString);
    id = (_params$get = params.get('id')) !== null && _params$get !== void 0 ? _params$get : undefined;
    packageVersionId = (_params$get2 = params.get('packageVersionId')) !== null && _params$get2 !== void 0 ? _params$get2 : undefined;
    solutionId = (_params$get3 = params.get('solutionId')) !== null && _params$get3 !== void 0 ? _params$get3 : undefined;
  }

  //We do not want to initialize the store each time we move through the steps so we are gonna check if solutionId or packageVersionId changed
  const solution = securityReviewStore.getState().solution;
  let currentPackageVersionId;
  if (!isApiSolutionType(solution.technology)) {
    currentPackageVersionId = solution.solution.version.packageVersionId;
  }
  if (!solution.id || solutionId && solutionId !== solution.id || packageVersionId && !isApiSolutionType(solution.technology) && currentPackageVersionId !== packageVersionId) {
    securityReviewStore.initialize({
      id,
      solutionId,
      packageVersionId
    });
  }
  scrollToTop();
}
function checkShouldChange() {
  const {
    url
  } = routerStore.getState();
  const {
    publisher
  } = publisherStore.getState();
  if (!publisher || !url || !SECURITY_REVIEW_EDITOR_ROUTE.includes(url)) {
    return;
  }
  if (!securityReviewStore.getState().hasChanged && !securityReviewStore.getState().isLoading) {
    securityReviewStore.change();
  }
}
export const securityReviewStoreValidator = new SecurityReviewValidator(initialState.securityReview);
function updateSecurityReviewStoreValidator() {
  securityReviewStoreValidator.securityReview = selectSecurityReview(securityReviewStore.getState());
  securityReviewStoreValidator.resetCachedStepsCompleted();
  checkShouldChange();
}
securityReviewStore.subscribe(updateSecurityReviewStoreValidator, selectSecurityReview);
routerStore.subscribe(checkShouldInitialize, selectMatch, equalityFn);
publisherStore.subscribe(checkShouldInitialize, selectPublisher);